import { useEffect, useState } from "react";
import VehicleSelection from "./VehicleSelection";
import CommuteDistance from "./CommuteDistance";
import CommuteFrequency from "./CommuteFrequency";
import GetTheReport from "./GetTheReport";
import { useCalculator } from "../../hooks";
import useAPI from "../../api/useApi";
import { useQuery } from "@tanstack/react-query";

export interface formType {
  vehicle?: {
    country?: string;
    make?: string;
    model?: string;
    year?: string;
  };
  commute?: {
    departure_time?: string;
    frequency?: string;
    return_time?: string;
  };
  distance: {
    distance?: string;
    from?: string;
    to?: string;
  };
  user: {
    company?: string;
    company_email?: string;
    name?: string;
  };
}

export interface formScreenProps {
  form?: formType;
  setCurrentStage: React.Dispatch<React.SetStateAction<stages>>;
  onChange: (field: string, value: Record<string, string>) => void;
}

export default function Calculator() {
  const [currentStage, setCurrentStage] = useState(stages.vehicleSelection);
  //@ts-ignore
  const [form, setForm] = useState<formType>({ vehicle: {} });
  const { loading, getReport } = useCalculator();
  const progress =
    ((Object.values(stages).indexOf(currentStage) + 1) * 100) /
    Object.keys(stages).length;

  const handleChange = (field: string, c: Record<string, string | boolean>) => {
    //@ts-ignore
    setForm((form) => ({ ...form, [field]: { ...form[field], ...c } }));
    //@ts-ignore
  };

  const { getVehicles, getVehicleModels, getYearbyModel } = useAPI();
  const { data: vehicles = [] } = useQuery({
    queryKey: ["cars"],
    queryFn: getVehicles,
  });
  const { data: models = [] } = useQuery({
    queryKey: ["models", form.vehicle?.make],
    queryFn: () => getVehicleModels(form?.vehicle?.make),
    enabled: !!form.vehicle?.make,
  });
  const { data: years = [] } = useQuery({
    queryKey: ["years", form.vehicle?.make, form.vehicle?.model],
    queryFn: () => getYearbyModel(form?.vehicle?.make, form.vehicle?.model),
    enabled: !!form.vehicle?.make && !!form.vehicle.model,
  });

  useEffect(() => {
    setCurrentStage(stages.vehicleSelection);
  }, []);

  const renderScreen = () => {
    switch (currentStage) {
      case stages.vehicleSelection:
        return (
          <VehicleSelection
            setCurrentStage={setCurrentStage}
            onChange={handleChange}
            vehicles={vehicles}
            form={form}
            models={models}
            years={years}
          />
        );
      case stages.commuteDistance:
        return (
          <CommuteDistance
            setCurrentStage={setCurrentStage}
            onChange={handleChange}
          />
        );
      case stages.commuteFrequency:
        return (
          <CommuteFrequency
            setCurrentStage={setCurrentStage}
            onChange={handleChange}
          />
        );
      case stages.getTheReport:
        return (
          <GetTheReport
            setCurrentStage={setCurrentStage}
            onChange={handleChange}
            form={form}
            submit={() => getReport(form)}
          />
        );
      default:
        return (
          <VehicleSelection
            setCurrentStage={setCurrentStage}
            onChange={handleChange}
            vehicles={vehicles}
            models={models}
            years={years}
          />
        );
    }
  };
  return (
    <div className="flex flex-col justify-center">
      {/* Step Indicator and Progress Bar */}
      <div className="grid grid-cols-1 md:grid-cols-2 place-items-center mb-10">
        <div className="w-full xl:w-[70%] bg-gray-200 rounded-full h-1.5 self-center relative">
          <div
            className="bg-black h-1.5 rounded-full transition-all duration-500 ease-in-out"
            style={{ width: `${progress}%` }}
          />
          <p className="absolute top-full mt-2 text-center text-sm">
            Step {Object.values(stages).indexOf(currentStage) + 1}/
            {Object.keys(stages).length}
          </p>
        </div>
      </div>
      {renderScreen()}
    </div>
  );
}

export enum stages {
  vehicleSelection = "Vehicle Selection",
  commuteDistance = "Commute Distance",
  commuteFrequency = "Commute Frequency",
  getTheReport = "Get The Report",
}
